import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Box,
  FormControlLabel,
  IconButton,
  Modal,
  Switch,
  useTheme,
} from "@mui/material";
import Cookies from "js-cookie";
import { Typography, Link, Grid, Button } from "@mui/material";
import React, { useState } from "react";
import { BCPAngleDown, BCPXmark } from "../../icons/bcp/BCPIcons";
import { useSetCookieConsent } from "../../user/BCPAuthReducers";
import { getCookieTypes } from "./CookieFunctions";

const buttonSx = {
  borderRadius: 1,
  "text-transform": "none",
};

export default function CookieConsentForm({ authContext }) {
  const [renderForm, setRenderForm] = useState(
    authContext?.authState?.cookieConsent?.consented ? false : true
  );
  const [modalOpen, setModalOpen] = useState(false);

  const theme = useTheme();
  const setCookieConsent = useSetCookieConsent();

  if (!renderForm) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: Number.MAX_SAFE_INTEGER - 1,
      }}
    >
      <Box
        sx={{
          backgroundColor:
            theme?.palette?.mode === "dark" ? "#353535" : "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          boxShadow: 16,
          padding: 2,
          m: 1,
          borderRadius: 1,
          width: "100%",
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Cookie Consent
            </Typography>
            <Typography variant="body1">
              {`This website uses cookies to enhance your user experience, personalise content and advertising, and to analyze our traffic. By continuing to use this site you are agreeing to our use of cookies. Read our `}
              <Link href="/privacy" target="_blank" underline="none">
                Data Privacy Policy
              </Link>
              {` for more information, and to learn how to change your cookie settings.`}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  sx={buttonSx}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const consentObj = {
                      consented: true,
                      essential: true,
                      analytical: true,
                      performance: true,
                      informational: true,
                    };

                    Cookies.set("CookieConsent", JSON.stringify(consentObj));
                    setCookieConsent(consentObj);
                    setRenderForm(false);
                  }}
                >
                  Accept All
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  sx={buttonSx}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const consentObj = {
                      consented: true,
                      essential: true,
                      analytical: false,
                      performance: false,
                      informational: false,
                    };

                    Cookies.set("CookieConsent", JSON.stringify(consentObj));
                    setCookieConsent(consentObj);
                    setRenderForm(false);
                  }}
                >
                  Essential Only
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  sx={buttonSx}
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setModalOpen(true);
                  }}
                >
                  Cookie Settings
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <CookieSettingsModal
        open={modalOpen}
        setOpen={setModalOpen}
        buttonSx={buttonSx}
        setRenderForm={setRenderForm}
        theme={theme}
      />
    </Box>
  );
}

function CookieSettingsModal(props) {
  const { open, setOpen, buttonSx, setRenderForm, theme } = props;

  const cookieTypes = getCookieTypes();

  const setCookieConsent = useSetCookieConsent();

  const [customConsent, setCustomConsent] = useState({
    consented: true,
    essential: true,
    analytical: true,
    performance: true,
    informational: true,
  });

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: Number.MAX_SAFE_INTEGER - 1, overflowY: "scroll" }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          // maxHeight: "95vh",

          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 2,
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          <BCPXmark />
        </IconButton>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="h4" component="h2">
              Cookie Settings
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">
              {`You can manage your cookie preferences here. Please note that disabling certain cookies may affect the functionality of the website. `}
            </Typography>
          </Grid>
          {cookieTypes.map((cookieType) => (
            <Grid item xs={12}>
              <Accordion
                sx={{
                  width: "100%",
                  backgroundColor:
                    theme?.palette?.mode === "dark"
                      ? "background.secondary"
                      : "background.primary",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <BCPAngleDown
                      style={{
                        "font-size": "18px",
                        width: "18px",
                      }}
                    />
                  }
                  aria-controls={`${cookieType.name}-content`}
                  id={`${cookieType.name}-header`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <FormControlLabel
                    label={
                      <Typography className="MuiFormControlLabel-label">
                        {cookieType.name}
                      </Typography>
                    }
                    control={
                      <Switch
                        checked={
                          cookieType?.id === "essential"
                            ? true
                            : customConsent[cookieType.id]
                        }
                        onChange={() => {
                          setCustomConsent((prev) => ({
                            ...prev,
                            [cookieType.id]: !prev[cookieType.id],
                          }));
                        }}
                        disabled={cookieType?.id === "essential" ? true : false}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    {cookieType.description}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button
              fullWidth
              sx={buttonSx}
              variant="contained"
              color="primary"
              onClick={() => {
                Cookies.set("CookieConsent", JSON.stringify(customConsent));
                setCookieConsent(customConsent);
                setOpen(false);
                setRenderForm(false);
              }}
            >
              Confirm My Choices
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
